import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  Settings,
  User,
  // Calendar,
  UserPlus,
  Pencil,
  Trash2,
  Shield,
  Crown,
  AlertTriangle,
  Search,
  Archive,
  ArchiveRestore,
  LayoutGrid,
  CreditCard,
  ArrowUpRight,
  XCircle,
} from 'lucide-react';
import {
  selectAccount,
  selectAccountLoading,
  selectAccountError,
  selectDeleteUserLoading,
  selectDeleteUserSuccess,
  selectUpdateUserSuccess,
} from '../../store/accounts/accounts.selector';
import {
  getAccountRequest,
  deleteUserRequest,
  clearAccountState,
} from '../../store/accounts/accounts.action';
import { User as UserType } from '../../types/accounts/accounts.types';
import InviteUserToAccountModal from '../../components/common/InviteUserToAccountModal';
import Button from '../../components/ui/Button';
import { cn } from '../../utils/cn';
import Modal from '../../components/ui/Modal';
import { selectCurrentUser } from 'store/user';
import { IUserRole } from 'types';
import { useToast } from '../../contexts/ToastContext';
import {
  selectAdminWorkspaces,
  selectArchivedWorkspaces,
  selectGetAdminWorkspacesLoading,
  selectGetArchivedWorkspacesLoading,
  selectArchiveWorkspaceLoading,
  selectArchiveWorkspaceSuccess,
  selectGetAdminWorkspacesError,
  selectGetArchivedWorkspacesError,
  selectArchiveWorkspaceError,
} from '../../store/workspace/workspace.selector';
import {
  getAdminWorkspacesRequest,
  getArchivedWorkspacesRequest,
  archiveWorkspaceRequest,
  clearWorkspaceState,
} from '../../store/workspace/workspace.action';
import { IWorkspace } from '../../types';
import {
  cancelSubscriptionRequest,
  customerPortalRequest,
  selectCancelSubscriptionError,
  selectCustomerPortalError,
  selectCustomerPortalLoading,
  getCurrentPlanRequest,
  getPaymentUsageRequest,
  selectCancelSubscriptionLoading,
  selectCurrentPlan,
  selectCurrentPlanLoading,
  selectPaymentUsage,
  selectPaymentUsageLoading,
} from '../../store/payments';
import { normalizeSubscriptionStatus } from '../../services/paymentService/paymentService';

const AccountSettings: React.FC = () => {
  const dispatch = useDispatch<any>();
  const { showToast } = useToast();
  const account = useSelector(selectAccount);
  const loading = useSelector(selectAccountLoading);
  const error = useSelector(selectAccountError);
  const currentUser = useSelector(selectCurrentUser);
  const deleteUserLoading = useSelector(selectDeleteUserLoading);
  const deleteUserSuccess = useSelector(selectDeleteUserSuccess);
  const updateUserSuccess = useSelector(selectUpdateUserSuccess);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UserType | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [workspaceTab, setWorkspaceTab] = useState<'active' | 'archived'>(
    'active'
  );
  const [archiveWorkspaceModalOpen, setArchiveWorkspaceModalOpen] =
    useState(false);
  const [workspaceToArchive, setWorkspaceToArchive] =
    useState<IWorkspace | null>(null);
  const [cancelSubscriptionModalOpen, setCancelSubscriptionModalOpen] =
    useState(false);
  const [cancelSubscriptionRequested, setCancelSubscriptionRequested] =
    useState(false);

  const adminWorkspaces = useSelector(selectAdminWorkspaces);
  const archivedWorkspaces = useSelector(selectArchivedWorkspaces);
  const getAdminWorkspacesLoading = useSelector(
    selectGetAdminWorkspacesLoading
  );
  const getArchivedWorkspacesLoading = useSelector(
    selectGetArchivedWorkspacesLoading
  );
  const archiveWorkspaceLoading = useSelector(selectArchiveWorkspaceLoading);
  const archiveWorkspaceSuccess = useSelector(selectArchiveWorkspaceSuccess);
  const getAdminWorkspacesError = useSelector(selectGetAdminWorkspacesError);
  const getArchivedWorkspacesError = useSelector(
    selectGetArchivedWorkspacesError
  );
  const archiveWorkspaceError = useSelector(selectArchiveWorkspaceError);
  const cancelSubscriptionLoading = useSelector(
    selectCancelSubscriptionLoading
  );
  const cancelSubscriptionError = useSelector(selectCancelSubscriptionError);
  const customerPortalLoading = useSelector(selectCustomerPortalLoading);
  const customerPortalError = useSelector(selectCustomerPortalError);
  const paymentUsage = useSelector(selectPaymentUsage);
  const paymentUsageLoading = useSelector(selectPaymentUsageLoading);
  const currentPlan = useSelector(selectCurrentPlan);
  const currentPlanLoading = useSelector(selectCurrentPlanLoading);

  useEffect(() => {
    dispatch(getAccountRequest());
    dispatch(getAdminWorkspacesRequest({ isAdmin: true }));
    dispatch(getPaymentUsageRequest());
    dispatch(getCurrentPlanRequest());
  }, [dispatch]);

  useEffect(() => {
    if (workspaceTab === 'archived') {
      dispatch(getArchivedWorkspacesRequest());
    }
  }, [workspaceTab, dispatch]);

  useEffect(() => {
    if (archiveWorkspaceSuccess) {
      showToast({
        type: 'success',
        title: 'Workspace Archived',
        message: 'The workspace has been archived successfully.',
      });
      dispatch(clearWorkspaceState());
      setArchiveWorkspaceModalOpen(false);
      setWorkspaceToArchive(null);
    }
  }, [archiveWorkspaceSuccess, showToast, dispatch]);

  useEffect(() => {
    if (archiveWorkspaceError) {
      showToast({
        type: 'error',
        title: 'Archive Failed',
        message: archiveWorkspaceError,
      });
      dispatch(clearWorkspaceState());
    }
  }, [archiveWorkspaceError, showToast, dispatch]);

  useEffect(() => {
    if (getAdminWorkspacesError) {
      showToast({
        type: 'error',
        title: 'Failed to Load Workspaces',
        message: getAdminWorkspacesError,
      });
      dispatch(clearWorkspaceState());
    }
  }, [getAdminWorkspacesError, showToast, dispatch]);

  useEffect(() => {
    if (getArchivedWorkspacesError) {
      showToast({
        type: 'error',
        title: 'Failed to Load Archived Workspaces',
        message: getArchivedWorkspacesError,
      });
      dispatch(clearWorkspaceState());
    }
  }, [getArchivedWorkspacesError, showToast, dispatch]);

  useEffect(() => {
    if (!cancelSubscriptionRequested || cancelSubscriptionLoading) {
      return;
    }

    if (!cancelSubscriptionError) {
      setCancelSubscriptionModalOpen(false);
    }

    setCancelSubscriptionRequested(false);
  }, [
    cancelSubscriptionError,
    cancelSubscriptionLoading,
    cancelSubscriptionRequested,
  ]);

  useEffect(() => {
    if (!customerPortalError) {
      return;
    }
    showToast({
      type: 'error',
      title: 'Billing portal',
      message: customerPortalError,
    });
  }, [customerPortalError, showToast]);

  // Handle delete success
  useEffect(() => {
    if (deleteUserSuccess) {
      showToast({
        type: 'success',
        title: 'User Deleted',
        message: deleteUserSuccess,
      });
      dispatch(clearAccountState());
      dispatch(getAccountRequest());
      setDeleteModalOpen(false);
      setSelectedUser(null);
    }
  }, [deleteUserSuccess, showToast, dispatch]);

  // Handle update success
  useEffect(() => {
    if (updateUserSuccess) {
      showToast({
        type: 'success',
        title: 'User Updated',
        message: 'The user has been updated successfully.',
      });
      dispatch(clearAccountState());
      dispatch(getAccountRequest());
      setEditModalOpen(false);
      setSelectedUser(null);
    }
  }, [updateUserSuccess, showToast, dispatch]);

  const handleEditUser = (user: UserType) => {
    setSelectedUser(user);
    setEditModalOpen(true);
  };

  const handleDeleteUser = (user: UserType) => {
    setSelectedUser(user);
    setDeleteModalOpen(true);
  };

  const confirmDeleteUser = () => {
    if (selectedUser) {
      dispatch(deleteUserRequest(selectedUser._id));
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const getUserInitials = (user: UserType) => {
    const firstName = user.first_name || '';
    const lastName = user.last_name || '';
    if (firstName && lastName) {
      return `${firstName[0]}${lastName[0]}`.toUpperCase();
    } else if (firstName) {
      return firstName[0].toUpperCase();
    } else if (user.email) {
      return user.email[0].toUpperCase();
    }
    return 'U';
  };

  const isOwner = (user: UserType) => account?.owner === user._id;

  const getRoleIcon = (user: UserType) => {
    if (isOwner(user)) return <Crown className="w-4 h-4" />;
    if (user.role === IUserRole.ADMIN) return <Shield className="w-4 h-4" />;
    return <User className="w-4 h-4" />;
  };

  const getRoleBadgeColor = (user: UserType) => {
    if (isOwner(user)) return 'bg-primary/10 text-primary border-primary/30';
    if (user.role === IUserRole.ADMIN)
      return 'bg-info-background text-info border-info/30';
    return 'bg-muted text-muted-foreground border-border';
  };

  const getRoleDisplayName = (user: UserType) => {
    if (isOwner(user)) return 'Owner';
    if (user.role === IUserRole.ADMIN) return 'Admin';
    return 'Member';
  };

  const handleArchiveWorkspace = (workspace: IWorkspace) => {
    setWorkspaceToArchive(workspace);
    setArchiveWorkspaceModalOpen(true);
  };

  const confirmArchiveWorkspace = () => {
    if (workspaceToArchive?._id) {
      dispatch(archiveWorkspaceRequest(workspaceToArchive._id));
    }
  };

  const handleCancelSubscriptionClick = () => {
    setCancelSubscriptionModalOpen(true);
  };

  const confirmCancelSubscription = () => {
    setCancelSubscriptionRequested(true);
    dispatch(cancelSubscriptionRequest());
  };

  const getFilteredUsers = () => {
    if (!account?.invitedUsers?.length) return [];
    if (!searchTerm) return account.invitedUsers;
    const term = searchTerm.toLowerCase();
    return account.invitedUsers.filter(
      (user) =>
        user.first_name?.toLowerCase().includes(term) ||
        user.last_name?.toLowerCase().includes(term) ||
        user.email?.toLowerCase().includes(term) ||
        getRoleDisplayName(user).toLowerCase().includes(term)
    );
  };

  const isCurrentUserAccountOwner = Boolean(
    currentUser?._id && account?.owner === currentUser._id
  );

  const isCurrentUserInvitedAccountAdmin = Boolean(
    currentUser?._id &&
      account?.invitedUsers?.some(
        (invited) =>
          invited._id === currentUser._id && invited.role === IUserRole.ADMIN
      )
  );

  const canManageAccountBillingAndPlan =
    isCurrentUserAccountOwner || isCurrentUserInvitedAccountAdmin;

  const canInviteOrManageInvitedUsers =
    isCurrentUserAccountOwner ||
    isCurrentUserInvitedAccountAdmin ||
    currentUser?.role === IUserRole.ADMIN;

  const resolvedPlanName = currentPlan?.plan?.trim();
  const hasCurrentPaidPlan = Boolean(resolvedPlanName);
  const subscriptionStatus = normalizeSubscriptionStatus(currentPlan?.status);
  const isPlanProcessing = subscriptionStatus === 'processing';
  const isPlanOnHold = subscriptionStatus === 'on_hold';
  const planName =
    currentPlanLoading && currentPlan === null
      ? 'Loading…'
      : hasCurrentPaidPlan
        ? resolvedPlanName!
        : 'Free';
  const planPendingUsage = Boolean(paymentUsage?.pendingUsage);
  const isFreePlan =
    !hasCurrentPaidPlan || resolvedPlanName?.toLowerCase() === 'free';
  const showPlanPeriod =
    hasCurrentPaidPlan &&
    Boolean(currentPlan?.planStarts && currentPlan?.planEnds);
  const showCancelSubscription = !isFreePlan && subscriptionStatus === 'active';
  const showRevokeSubscription = !isFreePlan && isPlanOnHold;

  const getUsagePercent = (used: number, limit: number | null) => {
    if (limit === null) return 100;
    if (limit <= 0) return 0;
    return Math.min(100, Math.round((used / limit) * 100));
  };

  const usageRows = [
    {
      label: 'Emails this period',
      used: paymentUsage?.emailSentHistory ?? 0,
      limit: paymentUsage?.planLimits?.emailSentHistory ?? null,
    },
    {
      label: 'Campaigns',
      used: paymentUsage?.campaignCount ?? 0,
      limit: paymentUsage?.planLimits?.campaign ?? null,
    },
    {
      label: 'Segments',
      used: paymentUsage?.segmentCount ?? 0,
      limit: paymentUsage?.planLimits?.segment ?? null,
    },
    {
      label: 'Contacts',
      used: paymentUsage?.contactCount ?? 0,
      limit: paymentUsage?.planLimits?.contact ?? null,
    },
    {
      label: 'Workspaces',
      used: paymentUsage?.workspaceCount ?? 0,
      limit: paymentUsage?.planLimits?.workspace ?? null,
    },
  ].map((row) => ({
    ...row,
    pct: getUsagePercent(row.used, row.limit),
    aria:
      row.limit === null
        ? `${row.used} ${row.label.toLowerCase()} used, unlimited plan`
        : `${row.used} of ${row.limit} ${row.label.toLowerCase()} used`,
  }));

  return (
    <div className="p-4 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="mb-4">
        <h1 className="text-xl font-bold text-foreground mb-1">
          Account Settings
        </h1>
        <p className="text-sm text-muted-foreground">
          Manage your account preferences and invited users
        </p>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4" />
            <p className="text-sm text-muted-foreground">
              Loading account information...
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && !loading && (
        <div className="bg-card rounded-xl border border-destructive/50 p-4 shadow-sm">
          <div className="flex items-center text-destructive">
            <Settings className="w-4 h-4 mr-2 shrink-0" />
            <p className="text-sm font-medium">Error loading account</p>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{error}</p>
        </div>
      )}

      {/* Account Information */}
      {!loading && account && (
        <div className="space-y-6">
          {/* Account Details Section */}
          {/* <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <Settings className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Account Details
              </h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center text-sm gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Created:</span>
                <span className="text-foreground font-medium">
                  {formatDate(account.createdAt)}
                </span>
              </div>
              <div className="flex items-center text-sm gap-2">
                <Calendar className="w-4 h-4 text-muted-foreground shrink-0" />
                <span className="text-muted-foreground">Last Updated:</span>
                <span className="text-foreground font-medium">
                  {formatDate(account.updatedAt)}
                </span>
              </div>
            </div>
          </div> */}

          {canManageAccountBillingAndPlan && (
            <div className="relative overflow-hidden rounded-2xl border border-border bg-card shadow-sm">
              <div className="relative p-6 sm:p-7">
                <div className="flex flex-col gap-5 sm:flex-row sm:items-start sm:justify-between sm:gap-6">
                  <div className="flex items-start gap-4">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/12 ring-1 ring-primary/15">
                      <CreditCard className="h-5 w-5 text-primary" />
                    </div>
                    <div>
                      <div className="flex flex-wrap items-center gap-2">
                        <h2 className="text-base font-semibold text-foreground">
                          Plan & usage
                        </h2>
                        <span className="rounded-md bg-muted px-2 py-0.5 text-xs font-medium text-muted-foreground">
                          {planName}
                        </span>
                        {planPendingUsage && (
                          <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-700">
                            Pending usage sync
                          </span>
                        )}
                        {isPlanProcessing && hasCurrentPaidPlan && (
                          <span className="rounded-md bg-sky-500/10 px-2 py-0.5 text-xs font-medium text-sky-800">
                            Processing
                          </span>
                        )}
                        {isPlanOnHold && hasCurrentPaidPlan && (
                          <span className="rounded-md bg-amber-500/10 px-2 py-0.5 text-xs font-medium text-amber-800">
                            On hold
                          </span>
                        )}
                      </div>
                      <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                        {paymentUsageLoading
                          ? 'Loading usage data...'
                          : `Track billing period usage and current plan limits. Compare plans anytime.`}
                      </p>
                      {isPlanProcessing && hasCurrentPaidPlan && (
                        <p className="mt-2 text-sm leading-relaxed text-sky-900/90">
                          Your plan is processing and should be fully active
                          within 24 hours.
                        </p>
                      )}
                      {showPlanPeriod && (
                        <p className="mt-2 text-xs text-muted-foreground">
                          Billing period: {formatDate(currentPlan!.planStarts!)}
                          {' - '}
                          {formatDate(currentPlan!.planEnds!)}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex w-full flex-row flex-wrap items-center justify-end gap-2 sm:w-auto sm:flex-nowrap">
                    <Link
                      to="/upgrade-plans"
                      className={cn(
                        'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-all',
                        'border border-border bg-background text-foreground shadow-sm',
                        'hover:border-primary/30 hover:bg-primary/5 hover:shadow',
                        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-card',
                        'px-3 py-2 text-xs shrink-0'
                      )}
                      aria-label="Upgrade plan"
                    >
                      <ArrowUpRight className="h-3.5 w-3.5" />
                      Upgrade plan
                    </Link>
                    {showCancelSubscription && (
                      <Button
                        type="button"
                        variant="danger"
                        size="small"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold shadow-sm shrink-0"
                        onClick={handleCancelSubscriptionClick}
                        disabled={cancelSubscriptionLoading}
                      >
                        {cancelSubscriptionLoading ? (
                          <>
                            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            Redirecting...
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5" />
                            Cancel
                          </>
                        )}
                      </Button>
                    )}
                    {showRevokeSubscription && (
                      <Button
                        type="button"
                        variant="outline"
                        size="small"
                        className="inline-flex items-center justify-center gap-1.5 rounded-lg border-destructive/40 px-3 py-2 text-xs font-semibold text-destructive shadow-sm hover:bg-destructive/5 shrink-0"
                        onClick={() => dispatch(customerPortalRequest())}
                        disabled={customerPortalLoading}
                      >
                        {customerPortalLoading ? (
                          <>
                            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
                            Opening…
                          </>
                        ) : (
                          <>
                            <XCircle className="h-3.5 w-3.5" />
                            Revoke subscription
                          </>
                        )}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="mt-8 grid gap-6 grid-cols-3 sm:gap-5">
                  {usageRows.map((row) => (
                    <div
                      key={row.label}
                      className="rounded-xl border border-border/80 bg-background/60 p-4 backdrop-blur-[2px]"
                    >
                      <div className="flex items-baseline justify-between gap-2 text-sm">
                        <span className="font-medium text-foreground">
                          {row.label}
                        </span>
                        <span className="tabular-nums text-xs font-semibold text-muted-foreground">
                          {row.limit === null
                            ? `${row.used} / Unlimited`
                            : `${row.used} / ${row.limit}`}
                        </span>
                      </div>
                      <div
                        className="mt-3 h-2 w-full overflow-hidden rounded-full bg-muted"
                        role="progressbar"
                        aria-valuenow={row.used}
                        aria-valuemin={0}
                        aria-valuemax={row.limit ?? row.used}
                        aria-label={row.aria}
                      >
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-primary to-primary/85 transition-all duration-500 ease-out"
                          style={{ width: `${row.pct}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Workspaces Section */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                <LayoutGrid className="w-5 h-5 text-primary" />
              </div>
              <h2 className="text-base font-semibold text-foreground">
                Workspaces
              </h2>
            </div>

            <div className="flex gap-1 p-1 bg-muted/50 rounded-lg mb-4 max-w-xs">
              <button
                onClick={() => setWorkspaceTab('active')}
                className={`flex justify-center items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full transition-all duration-200 ${
                  workspaceTab === 'active'
                    ? 'bg-primary/80 text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                }`}
              >
                Active
              </button>
              <button
                onClick={() => setWorkspaceTab('archived')}
                className={`flex justify-center items-center gap-2 px-3 py-2 rounded-md text-sm font-medium w-full transition-all duration-200 ${
                  workspaceTab === 'archived'
                    ? 'bg-primary/80 text-primary-foreground shadow-sm'
                    : 'text-muted-foreground hover:text-foreground hover:bg-primary/10'
                }`}
              >
                Archived
              </button>
            </div>

            {workspaceTab === 'active' ? (
              getAdminWorkspacesLoading ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4" />
                  <p className="text-sm text-muted-foreground">
                    Loading workspaces...
                  </p>
                </div>
              ) : !adminWorkspaces?.length ? (
                <div className="text-center py-12">
                  <LayoutGrid className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">
                    No active workspaces
                  </p>
                </div>
              ) : (
                <div className="overflow-x-auto rounded-xl border border-border">
                  <table className="min-w-full divide-y divide-border">
                    <thead>
                      <tr className="bg-muted/50">
                        <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                          Name
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-medium text-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {adminWorkspaces.map((workspace, index) => (
                        <tr
                          key={workspace._id || index}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-sm font-medium text-foreground">
                            {workspace.name}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-right">
                            <Button
                              variant="ghost"
                              size="small"
                              onClick={() => handleArchiveWorkspace(workspace)}
                              className="p-1.5 text-muted-foreground hover:text-amber-600 hover:bg-amber-500/10 rounded-lg"
                              title="Archive workspace"
                              aria-label="Archive workspace"
                            >
                              <Archive className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )
            ) : getArchivedWorkspacesLoading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent mb-4" />
                <p className="text-sm text-muted-foreground">
                  Loading archived workspaces...
                </p>
              </div>
            ) : !archivedWorkspaces?.length ? (
              <div className="text-center py-12">
                <Archive className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  No archived workspaces
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        Name
                      </th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-foreground uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {archivedWorkspaces.map((workspace, index) => (
                      <tr
                        key={workspace._id || index}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 text-sm font-medium text-foreground">
                          {workspace.name}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <Button
                            variant="ghost"
                            size="small"
                            onClick={() =>
                              dispatch(archiveWorkspaceRequest(workspace._id!))
                            }
                            disabled={archiveWorkspaceLoading}
                            className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg"
                            title="Unarchive workspace"
                            aria-label="Unarchive workspace"
                          >
                            <ArchiveRestore className="w-4 h-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Invited Users Section */}
          <div className="bg-card rounded-xl border border-border p-6 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-info-background rounded-lg flex items-center justify-center">
                  <User className="w-5 h-5 text-info" />
                </div>
                <h2 className="text-base font-semibold text-foreground">
                  Invited Users
                </h2>
                {account.invitedUsers && account.invitedUsers.length > 0 && (
                  <span className="px-2.5 py-0.5 text-xs font-medium bg-muted text-muted-foreground rounded-full">
                    {account.invitedUsers.length}
                  </span>
                )}
              </div>
              <Button
                variant="primary"
                size="small"
                disabled={loading || !canInviteOrManageInvitedUsers}
                onClick={() => setInviteModalOpen(true)}
              >
                <UserPlus className="w-4 h-4 mr-2" />
                Invite User
              </Button>
            </div>

            {account.invitedUsers && account.invitedUsers.length > 0 && (
              <div className="mb-4">
                <div className="relative max-w-sm">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
                  <input
                    type="text"
                    placeholder="Search users..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 text-sm border border-border rounded-lg bg-background text-foreground placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary transition-colors"
                    aria-label="Search users"
                  />
                </div>
              </div>
            )}

            {getFilteredUsers().length === 0 ? (
              <div className="text-center py-12">
                <User className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-sm text-muted-foreground">
                  {searchTerm ? 'No users found' : 'No invited users yet'}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto rounded-xl border border-border">
                <table className="min-w-full divide-y divide-border">
                  <thead>
                    <tr className="bg-muted/50">
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        Email
                      </th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-foreground uppercase tracking-wider">
                        Role
                      </th>
                      {
                        <th className="px-4 py-3 text-right text-xs font-medium text-foreground uppercase tracking-wider">
                          Actions
                        </th>
                      }
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {getFilteredUsers().map((user, index) => (
                      <tr
                        key={user._id || index}
                        className="hover:bg-muted/30 transition-colors"
                      >
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0 h-9 w-9">
                              {user.profile_picture ? (
                                <img
                                  className="h-9 w-9 rounded-full object-cover ring-1 ring-border"
                                  src={user.profile_picture}
                                  alt={`${user.first_name} ${user.last_name}`}
                                />
                              ) : (
                                <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center ring-1 ring-border">
                                  <span className="text-xs font-medium text-muted-foreground">
                                    {getUserInitials(user)}
                                  </span>
                                </div>
                              )}
                            </div>
                            <div className="text-sm font-medium text-foreground">
                              {user.first_name} {user.last_name}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-muted-foreground">
                          {user.email}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <span
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${getRoleBadgeColor(user)}`}
                          >
                            {getRoleIcon(user)}
                            {getRoleDisplayName(user)}
                          </span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1">
                            {currentUser &&
                              currentUser._id !== user._id &&
                              (account?.owner === currentUser._id ||
                                isCurrentUserInvitedAccountAdmin ||
                                (currentUser.role === IUserRole.ADMIN &&
                                  user.role !== IUserRole.ADMIN &&
                                  !isOwner(user))) && (
                                <>
                                  <Button
                                    variant="ghost"
                                    size="small"
                                    onClick={() => handleEditUser(user)}
                                    className="p-1.5 text-muted-foreground hover:text-primary hover:bg-primary/10 rounded-lg"
                                    title="Edit user"
                                    aria-label="Edit user"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </Button>
                                  {(account?.owner === currentUser._id ||
                                    isCurrentUserInvitedAccountAdmin) && (
                                    <Button
                                      variant="ghost"
                                      size="small"
                                      onClick={() => handleDeleteUser(user)}
                                      className="p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive-background rounded-lg"
                                      title="Delete user"
                                      aria-label="Delete user"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  )}
                                </>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Invite/Edit User Modal */}
      <InviteUserToAccountModal
        isOpen={inviteModalOpen || editModalOpen}
        onClose={() => {
          if (inviteModalOpen) {
            setInviteModalOpen(false);
          }
          if (editModalOpen) {
            setEditModalOpen(false);
            setSelectedUser(null);
          }
        }}
        user={editModalOpen ? selectedUser : null}
      />

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={deleteModalOpen}
        onClose={() => {
          if (!deleteUserLoading) {
            setDeleteModalOpen(false);
            setSelectedUser(null);
          }
        }}
        title="Delete User"
        size="small"
      >
        {selectedUser && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-destructive-background border border-destructive/50 rounded-xl">
              <div className="flex-shrink-0">
                <AlertTriangle className="w-5 h-5 text-destructive" />
              </div>
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">
                  Are you sure you want to delete this user?
                </h3>
                <p className="text-sm text-muted-foreground">
                  This action cannot be undone. The user will lose access to
                  this account immediately.
                </p>
              </div>
            </div>

            <div className="bg-muted/30 rounded-xl p-4 border border-border">
              <div className="flex items-center gap-3">
                <div className="flex-shrink-0 h-10 w-10">
                  {selectedUser.profile_picture ? (
                    <img
                      className="h-10 w-10 rounded-full object-cover ring-1 ring-border"
                      src={selectedUser.profile_picture}
                      alt={`${selectedUser.first_name} ${selectedUser.last_name}`}
                    />
                  ) : (
                    <div className="h-10 w-10 rounded-full bg-muted flex items-center justify-center ring-1 ring-border">
                      <span className="text-sm font-medium text-muted-foreground">
                        {getUserInitials(selectedUser)}
                      </span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">
                    {selectedUser.first_name} {selectedUser.last_name}
                  </div>
                  <div className="text-sm text-muted-foreground">
                    {selectedUser.email}
                  </div>
                  <div
                    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border mt-1.5 ${getRoleBadgeColor(selectedUser)}`}
                  >
                    {getRoleIcon(selectedUser)}
                    {getRoleDisplayName(selectedUser)}
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setDeleteModalOpen(false);
                  setSelectedUser(null);
                }}
                disabled={deleteUserLoading}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={confirmDeleteUser}
                disabled={deleteUserLoading}
                size="small"
                className="flex items-center gap-2"
              >
                {deleteUserLoading && (
                  <div className="w-4 h-4 border-2 border-destructive-foreground border-t-transparent rounded-full animate-spin shrink-0" />
                )}
                {deleteUserLoading ? 'Deleting...' : 'Delete User'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Archive Workspace Confirmation Modal */}
      <Modal
        isOpen={archiveWorkspaceModalOpen}
        onClose={() => {
          if (!archiveWorkspaceLoading) {
            setArchiveWorkspaceModalOpen(false);
            setWorkspaceToArchive(null);
          }
        }}
        title="Archive Workspace"
        size="small"
      >
        {workspaceToArchive && (
          <div className="space-y-4">
            <div className="flex items-start gap-3 p-4 bg-muted/50 border border-border rounded-xl">
              <Archive className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-medium text-foreground mb-1">
                  Archive &quot;{workspaceToArchive.name}&quot;?
                </h3>
                <p className="text-sm text-muted-foreground">
                  This workspace will be moved to archived. You can view it in
                  the Archived tab.
                </p>
              </div>
            </div>
            <div className="flex items-center justify-end gap-3 pt-4 border-t border-border">
              <Button
                variant="outline"
                onClick={() => {
                  setArchiveWorkspaceModalOpen(false);
                  setWorkspaceToArchive(null);
                }}
                disabled={archiveWorkspaceLoading}
                size="small"
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                onClick={confirmArchiveWorkspace}
                disabled={archiveWorkspaceLoading}
                size="small"
                className="flex items-center gap-2"
              >
                {archiveWorkspaceLoading && (
                  <div className="w-4 h-4 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin shrink-0" />
                )}
                {archiveWorkspaceLoading ? 'Archiving...' : 'Archive'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {/* Cancel Subscription Confirmation Modal */}
      <Modal
        isOpen={cancelSubscriptionModalOpen}
        onClose={() => {
          if (!cancelSubscriptionLoading) {
            setCancelSubscriptionModalOpen(false);
          }
        }}
        title="Cancel Subscription"
        size="small"
      >
        <div className="space-y-4">
          <div className="flex items-start gap-3 rounded-xl border border-destructive/50 bg-destructive-background p-4">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" />
            <div>
              <h3 className="mb-1 text-sm font-medium text-foreground">
                Are you sure you want to cancel your subscription?
              </h3>
              <p className="text-sm text-muted-foreground">
                You will lose access to paid features at the end of your current
                billing period.
              </p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
            <Button
              variant="outline"
              onClick={() => setCancelSubscriptionModalOpen(false)}
              disabled={cancelSubscriptionLoading}
              size="small"
            >
              Keep Subscription
            </Button>
            <Button
              variant="danger"
              onClick={confirmCancelSubscription}
              disabled={cancelSubscriptionLoading}
              size="small"
              className="flex items-center gap-2"
            >
              {cancelSubscriptionLoading && (
                <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-destructive-foreground border-t-transparent" />
              )}
              {cancelSubscriptionLoading ? 'Cancelling...' : 'Yes, Cancel'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* No Data State */}
      {!loading && !error && !account && (
        <div className="bg-card rounded-xl border border-border p-8 shadow-sm">
          <div className="flex flex-col items-center justify-center text-center">
            <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
              <Settings className="w-6 h-6 text-primary" />
            </div>
            <h2 className="text-lg font-semibold text-foreground mb-1">
              No Account Data
            </h2>
            <p className="text-sm text-muted-foreground max-w-md">
              Unable to load account information. Please try refreshing the
              page.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default AccountSettings;
